"use client";
import { adjustViewportForIOS } from "@/lib/viewport";
import React, { useEffect } from "react";
import {
  IconChevronDown,
  IconCalendarEvent,
  IconChartLine,
  IconTool,
  IconCards,
} from "@tabler/icons-react";
import {
  Chip,
  Navbar,
  NavbarBrand,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
  NavbarContent,
  NavbarItem,
  Button,
  Link,
} from "@heroui/react";
import {
  Dropdown,
  DropdownItem,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
} from "@heroui/dropdown";

import Image from "next/image";
import AutocompleteSearchbar from "@/components/Searchbar/AutocompleteSearchbar";
import { Suspense } from "react";
import { Accordion, AccordionItem } from "@heroui/react";
import { usePathname, useRouter } from "next/navigation";
import { SupportedGames } from "@/types/game";

import { useSeriesStore } from "@/stores/seriesStore";
import { CDN } from "@/lib/cdn";
import { GAMES, getGameNavigationConfig, getRoutePrefix } from "@/games";
import GameSelector from "@/components/GameSelector";

// Create a runtime array of supported games that matches the SupportedGames type
const SUPPORTED_GAMES = GAMES.map((g) => g.id) as readonly SupportedGames[];

// Type guard to check if a string is a valid SupportedGames value
const isValidGame = (game: string): game is SupportedGames => {
  return SUPPORTED_GAMES.includes(game as SupportedGames);
};

/**
 * Where a set's card list lives for the current game.
 *
 * One Piece owns the un-prefixed root routes on gumgum.gg, so it keeps
 * /cards/<set>; every other game is prefixed, /<game>/cards/<set>. This was
 * written out at each of the four call sites (two menus x boosters/starters),
 * and two of them had "/union-arena/" hardcoded, so every game's desktop
 * dropdown linked into Union Arena.
 */
const getSetCardsHref = (
  currentGame: SupportedGames,
  setName: string,
  getGroupLink: (name: string) => string,
): string =>
  currentGame === "one-piece"
    ? getGroupLink(setName)
    : `/${getRoutePrefix(currentGame)}cards/${setName}`;

// Game-specific series data functions
const getOnePieceSeries = (
  series: any[],
  getFormatName: (name: string) => string,
  getGroupLink: (name: string) => string,
) => {
  // Filter out starter decks and promo cards, keep only booster sets
  const boosterSets = series.filter((s) => {
    const name = s.name;
    // Exclude starter decks (ST prefix) and promos (P)
    return !name.startsWith("ST") && name !== "P";
  });

  // Sort by set number in descending order (newest first)
  // Custom ordering: EB05 should appear right after OP17
  boosterSets.sort((a, b) => {
    const aName = a.name;
    const bName = b.name;

    // Custom ordering map for specific positioning
    const customOrder: Record<string, number> = {
      EB05: 1001, // Latest set - at the top
      OP17: 1000,
      OP16: 998,
    };

    // Check if either has a custom order
    const aCustom = customOrder[aName];
    const bCustom = customOrder[bName];

    if (aCustom !== undefined && bCustom !== undefined) {
      return bCustom - aCustom;
    }
    if (aCustom !== undefined) return -1;
    if (bCustom !== undefined) return 1;

    // Extract the main number from set names like "OP16", "EB04", "PRB02", etc.
    const getSetNumber = (name: string) => {
      // Match patterns like OP16, EB04, PRB02
      const match = name.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    };

    // Get set type priority (OP > EB > PRB)
    const getSetTypePriority = (name: string) => {
      if (name.startsWith("OP")) return 3;
      if (name.startsWith("EB")) return 2;
      if (name.startsWith("PRB")) return 1;
      return 0;
    };

    const aNum = getSetNumber(aName);
    const bNum = getSetNumber(bName);
    const aPriority = getSetTypePriority(aName);
    const bPriority = getSetTypePriority(bName);

    // If same number, sort by type priority
    if (aNum === bNum) {
      return bPriority - aPriority;
    }

    // Otherwise sort by number descending
    return bNum - aNum;
  });

  return boosterSets;
};

const getOnePieceStarterDecks = (series: any[]) => {
  let starterDecks = [];
  for (const i in series) {
    if (series[i]["name"].includes("ST")) {
      starterDecks.push(series[i]);
    }
  }
  // Sort starter decks numerically
  starterDecks.sort((a, b) => {
    const numA = parseInt(a.name.replace("ST", ""));
    const numB = parseInt(b.name.replace("ST", ""));
    return numB - numA;
  });
  return starterDecks;
};

const getUnionArenaSeries = () => {
  // Union Arena sets from the data
  const sets = [
    { name: "BLC", setName: "Bleach: Thousand-Year Blood War" },
    { name: "HTR", setName: "Hunter X Hunter" },
    { name: "JJK", setName: "Jujutsu Kaisen" },
    { name: "CGH", setName: "Code Geass Lelouch of the Rebellion" },
    { name: "KMY", setName: "Demon Slayer: Kimetsu no Yaiba" },
    { name: "OPM", setName: "One Punch Man" },
    { name: "SAO", setName: "Sword Art Online" },
    { name: "BCV", setName: "Black Clover" },
    { name: "KIN", setName: "Kinnikuman" },
    { name: "REZ", setName: "Re:Zero - Starting Life in Another World" },
  ];
  return sets;
};

/**
 * Palworld's sets come from the database via /api/games/palworld/sets, not from
 * the One Piece series blob, so its renderer just passes through what the
 * navbar fetched. Boosters (EBP) and trial decks (ETD) are split so they land
 * in the same two menus One Piece uses.
 */
const getPalworldSeries = (
  _series: any[],
  _getFormatName: unknown,
  _getGroupLink: unknown,
  fetched: any[] = [],
) => fetched.filter((s) => !s.name.startsWith("ETD"));

const getPalworldStarterDecks = (_series: any[], fetched: any[] = []) =>
  fetched.filter((s) => s.name.startsWith("ETD"));

const getUnionArenaStarterDecks = () => {
  // Union Arena doesn't have traditional starter decks like One Piece
  // Return empty array for now
  return [];
};

// Configuration for navigation items per game
/**
 * Navbar-local renderers only. Every show* flag and marketWatchHref now comes
 * from games/<game>/config.ts via getGameNavigationConfig - this object used to
 * duplicate all of them, so a new game had to be registered twice.
 */
const gameSeriesRenderers: Partial<
  Record<SupportedGames, { getSeries: any; getStarterDecks: any }>
> = {
  "one-piece": {
    getSeries: getOnePieceSeries,
    getStarterDecks: getOnePieceStarterDecks,
  },
  "union-arena": {
    getSeries: getUnionArenaSeries,
    getStarterDecks: getUnionArenaStarterDecks,
  },
  palworld: {
    getSeries: getPalworldSeries,
    getStarterDecks: getPalworldStarterDecks,
  },
};

/** Games whose set list is fetched from the API rather than derived locally. */
const GAMES_WITH_FETCHED_SETS: string[] = ["palworld"];

function getNavRenderers(game: SupportedGames) {
  return gameSeriesRenderers[game] ?? gameSeriesRenderers["one-piece"]!;
}

export default function App() {
  const pathname = usePathname();
  const isOnePiecePath = pathname.startsWith("/one_piece/");
  const websiteConfig = process.env.NEXT_PUBLIC_WEBSITE_CONFIG;

  const series = useSeriesStore((state) => state.series);
  const getFormatName = useSeriesStore((state) => state.getFormatName);
  const getGroupLink = useSeriesStore((state) => state.getGroupLink);
  const fetchSeries = useSeriesStore((state) => state.fetchSeries);

  // Fetch series data on mount
  React.useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  // Extract game name from pathname
  const getCurrentGame = (): SupportedGames => {
    const pathSegments = pathname.split("/").filter(Boolean);
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0];
      if (isValidGame(firstSegment)) {
        return firstSegment;
      }
    }
    // Default to one-piece if no valid game in path
    return "one-piece";
  };

  const currentGame = getCurrentGame();
  const navConfig = getGameNavigationConfig(currentGame);
  const navRenderers = getNavRenderers(currentGame);

  useEffect(() => {
    // hack that prevents iphone from zooming in, but doesnt mess up android
    adjustViewportForIOS();
  }, [pathname]);

  useEffect(() => {});

  const icons = {
    chevron: <IconChevronDown size={18} />,
  };

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  // Sets for games that keep their cards in the database rather than in the
  // One Piece series blob.
  const [fetchedSets, setFetchedSets] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (!GAMES_WITH_FETCHED_SETS.includes(currentGame)) {
      setFetchedSets([]);
      return;
    }

    let cancelled = false;

    fetch(`/api/games/${currentGame}/sets`)
      .then((res) => (res.ok ? res.json() : []))
      .then((sets) => {
        if (!cancelled) setFetchedSets(Array.isArray(sets) ? sets : []);
      })
      .catch(() => {
        if (!cancelled) setFetchedSets([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentGame]);

  // Get game-specific series and starter decks
  const gameSeries = navRenderers.getSeries(
    series,
    getFormatName,
    getGroupLink,
    fetchedSets,
  );
  const gameStarterDecks = navRenderers.getStarterDecks(series, fetchedSets);

  let boostersMobile = [];
  for (const s of gameSeries) {
    boostersMobile.push(
      <Link
        key={s.name}
        color="foreground"
        className="my-3"
        href={getSetCardsHref(currentGame, s.name, getGroupLink)}
      >
        <div className="w-10 mr-4 text-center">{s.name}</div>
        <div>{s.setName}</div>
      </Link>,
    );
  }

  // Only add promo link for One Piece
  if (currentGame === "one-piece") {
    boostersMobile.push(
      <Link
        key={"promo"}
        color="foreground"
        className="my-3"
        href={getGroupLink("P")}
      >
        <div className="w-10 mr-4">P</div>
        <div>Promo Cards</div>
      </Link>,
    );
  }

  let startersMobile = [];
  for (const s of gameStarterDecks) {
    startersMobile.push(
      <Link
        key={s.name}
        color="foreground"
        className="my-3"
        href={getSetCardsHref(currentGame, s.name, getGroupLink)}
      >
        <div className="w-10 mr-4 text-center">{s.name}</div>
        <div>{s.setName}</div>
      </Link>,
    );
  }

  // For TCGfan landing page only, show logo and game selector
  if (websiteConfig === "tcgfan" && pathname === "/") {
    return (
      <Navbar
        id="navbar"
        position="static"
        className="flex relative"
        maxWidth="lg"
      >
        <NavbarContent justify="start" className="gap-4">
          <BrandLogoLink>
            <NavbarBrand>
              <BrandLogo />
            </NavbarBrand>
          </BrandLogoLink>
          <NavbarItem>
            <GameSelector curGame={currentGame} />
          </NavbarItem>
        </NavbarContent>
      </Navbar>
    );
  }

  // For gumgum, show full navigation
  return (
    <Navbar
      id="navbar"
      position="static"
      onMenuOpenChange={setIsMenuOpen}
      className="flex relative"
      maxWidth={currentGame === "one-piece" ? "xl" : "lg"}
    >
      <NavbarContent justify="start">
        <BrandLogoLink>
          <NavbarBrand>
            <BrandLogo />
          </NavbarBrand>
        </BrandLogoLink>
        {(currentGame === "naruto" || currentGame === "palworld") && (
          <NavbarItem className="hidden sm:flex">
            <GameSelector curGame={currentGame} />
          </NavbarItem>
        )}
        <NavbarItem className="sm:hidden">
          {currentGame === "one-piece" && (
            <Suspense>
              <AutocompleteSearchbar placeholder="3c 5p Red Zoro" />
            </Suspense>
          )}
        </NavbarItem>
        <NavbarMenuToggle
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          className="sm:hidden ml-auto"
        />
        <NavbarContent
          as="div"
          className="items-center hidden sm:flex"
          justify="end"
        >
          <NavbarItem>
            <Link
              color="foreground"
              href={`/${currentGame}/events`}
              className="text-white text-sm font-normal py-2"
            >
              <div className="flex h-full items-center gap-1">
                <IconCalendarEvent size={18} />
                <span>Events</span>
              </div>
            </Link>
          </NavbarItem>

          {navConfig.showMarketWatch && (
            <NavbarItem>
              <Link
                color="foreground"
                href={navConfig.marketWatchHref}
                className="text-white text-sm font-normal py-2"
              >
                <div className="flex h-full items-center gap-1">
                  <IconChartLine size={18} />
                  <span>Market Watch</span>
                </div>
              </Link>
            </NavbarItem>
          )}

          {!navConfig.showTools && navConfig.deckbuilderHref && (
            <NavbarItem>
              <Link
                color="foreground"
                href={navConfig.deckbuilderHref}
                className="text-white text-sm font-normal py-2"
              >
                <div className="flex h-full items-center gap-1">
                  <IconTool size={18} />
                  <span>Deckbuilder</span>
                </div>
              </Link>
            </NavbarItem>
          )}

          {navConfig.showTools && (
            <Dropdown>
              <NavbarItem>
                <DropdownTrigger>
                  <Button
                    disableRipple
                    className="p-0 bg-transparent data-[hover=true]:bg-transparent"
                    startContent={<IconTool size={18} />}
                    endContent={icons.chevron}
                    radius="sm"
                    variant="light"
                  >
                    Tools
                  </Button>
                </DropdownTrigger>
              </NavbarItem>
              <DropdownMenu
                aria-label="Tools"
                classNames={{
                  base: "overflow-auto scrollbar-hide max-h-[80svh]",
                }}
                itemClasses={{
                  base: "gap-2",
                }}
              >
                <DropdownSection
                  title="Tools"
                  showDivider={false}
                  className="max-w-[300px]"
                >
                  <DropdownItem
                    key="deckbuilder"
                    href={navConfig.deckbuilderHref}
                    description="Build a deck with OPTCG cards"
                  >
                    Deckbuilder
                  </DropdownItem>
                  <DropdownItem
                    key="hypergeometric-calculator"
                    href="/hypergeometric-calculator"
                    description="Calculator for determining probabilities of drawing cards"
                  >
                    Hypergeometric Calculator
                  </DropdownItem>
                </DropdownSection>
              </DropdownMenu>
            </Dropdown>
          )}

          {(navConfig.showBoosters || navConfig.showStarters) && (
            <Dropdown>
              <NavbarItem>
                <DropdownTrigger>
                  <Button
                    disableRipple
                    className="p-0 bg-transparent data-[hover=true]:bg-transparent"
                    startContent={<IconCards size={18} />}
                    endContent={icons.chevron}
                    radius="sm"
                    variant="light"
                  >
                    Cards
                  </Button>
                </DropdownTrigger>
              </NavbarItem>
              <DropdownMenu
                aria-label="Card Lists"
                classNames={{
                  base: "overflow-auto scrollbar-hide max-h-[80svh]",
                }}
                itemClasses={{
                  base: "gap-2",
                }}
              >
                {navConfig.showBoosters ? (
                  <DropdownSection
                    title="Boosters"
                    showDivider={
                      currentGame === "one-piece" && navConfig.showStarters
                    }
                  >
                    {gameSeries.map((s: any) => (
                      <DropdownItem
                        key={s.name}
                        description={s.setName}
                        href={getSetCardsHref(
                          currentGame,
                          s.name,
                          getGroupLink,
                        )}
                      >
                        {s.name}
                      </DropdownItem>
                    ))}
                  </DropdownSection>
                ) : null}
                {currentGame === "one-piece" ? (
                  <DropdownSection
                    title="Promos"
                    showDivider={navConfig.showStarters}
                  >
                    <DropdownItem
                      key="promo"
                      description="Promo Cards"
                      href={getGroupLink("P")}
                    >
                      P
                    </DropdownItem>
                  </DropdownSection>
                ) : null}
                {navConfig.showStarters ? (
                  <DropdownSection title="Starter Decks">
                    {gameStarterDecks.map((s: any) => (
                      <DropdownItem
                        key={s.name}
                        href={getSetCardsHref(
                          currentGame,
                          s.name,
                          getGroupLink,
                        )}
                        description={s.setName}
                      >
                        {s.name}
                      </DropdownItem>
                    ))}
                  </DropdownSection>
                ) : null}
              </DropdownMenu>
            </Dropdown>
          )}

          {navConfig.showSearch && (
            <NavbarItem>
              <Suspense>
                <AutocompleteSearchbar />
              </Suspense>
            </NavbarItem>
          )}
        </NavbarContent>

        <NavbarMenu>
          {(currentGame === "naruto" || currentGame === "palworld") && (
            <NavbarMenuItem>
              <div className="pt-4 pb-2 px-2">
                <GameSelector curGame={currentGame} />
              </div>
            </NavbarMenuItem>
          )}
          <NavbarMenuItem>
            <Link
              className="flex pt-4 pb-2 px-2 w-full h-full text-lg gap-1.5 items-center tap-highlight-transparent outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 transition-opacity"
              color="foreground"
              href={`/${currentGame}/events`}
            >
              <IconCalendarEvent size={20} />
              Events
            </Link>
          </NavbarMenuItem>

          {navConfig.showMarketWatch && (
            <NavbarMenuItem>
              <Link
                className="flex pt-4 pb-2 px-2 w-full h-full text-lg gap-1.5 items-center tap-highlight-transparent outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 transition-opacity"
                color="foreground"
                href={navConfig.marketWatchHref}
              >
                <IconChartLine size={20} />
                Market Watch
              </Link>
            </NavbarMenuItem>
          )}

          {navConfig.deckbuilderHref && (
            <NavbarMenuItem>
              <Link
                className="flex pt-4 pb-2 px-2 w-full h-full text-lg gap-1.5 items-center tap-highlight-transparent outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 transition-opacity"
                color="foreground"
                href={navConfig.deckbuilderHref}
              >
                <IconTool size={20} />
                Deckbuilder
              </Link>
            </NavbarMenuItem>
          )}

          {navConfig.showTools && (
            <NavbarMenuItem>
              <Link
                className="flex pt-4 pb-2 px-2 w-full h-full text-lg gap-1.5 items-center tap-highlight-transparent outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 transition-opacity"
                color="foreground"
                href="/hypergeometric-calculator"
              >
                <IconTool size={20} />
                Hypergeometric Calculator
              </Link>
            </NavbarMenuItem>
          )}

          {(navConfig.showBoosters || navConfig.showStarters) && (
            <NavbarMenuItem key={"Cards"}>
              <Accordion>
                {navConfig.showBoosters ? (
                  <AccordionItem key="1" aria-label="Boosters" title="Boosters">
                    <div className="flex flex-col">{boostersMobile}</div>
                  </AccordionItem>
                ) : null}
                {currentGame === "one-piece" ? (
                  <AccordionItem key="2" aria-label="Promos" title="Promos">
                    <div className="flex flex-col">
                      <Link
                        key={"promo"}
                        color="foreground"
                        className="my-3"
                        href={getGroupLink("P")}
                      >
                        <div className="w-10 mr-4">P</div>
                        <div>Promo Cards</div>
                      </Link>
                    </div>
                  </AccordionItem>
                ) : null}
                {navConfig.showStarters ? (
                  <AccordionItem
                    className="mb-20"
                    key="3"
                    aria-label="Starter Decks"
                    title="Starter Decks"
                  >
                    <div className="flex flex-col">{startersMobile}</div>
                  </AccordionItem>
                ) : null}
              </Accordion>
            </NavbarMenuItem>
          )}
        </NavbarMenu>
      </NavbarContent>
    </Navbar>
  );
}

function BrandLogoLink({ children }: { children: React.ReactNode }) {
  const websiteConfig = process.env.NEXT_PUBLIC_WEBSITE_CONFIG;

  if (websiteConfig === "tcgfan") {
    return <BrandLogoLinkClient>{children}</BrandLogoLinkClient>;
  }

  // For gumgum, just link to root
  return <Link href="/">{children}</Link>;
}

function BrandLogoLinkClient({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Always go back to landing page
    router.push("/");
  };

  return (
    <Link href="/" onClick={handleClick} className="cursor-pointer">
      {children}
    </Link>
  );
}

function BrandLogo() {
  if (process.env.NEXT_PUBLIC_WEBSITE_CONFIG === "tcgfan") {
    return (
      <Image
        src={`/logo/TCGfan/Logo-White.png`}
        loading="eager"
        alt="TCGfan.com"
        height={29}
        width={120}
      />
    );
  }

  return (
    <>
      <Image
        src={`${CDN}/shared/logo/gumgum.png`}
        loading="eager"
        alt="gumgum.gg OPTCG fan site"
        height={50}
        width={50}
        className="sm:mb-2"
      />
      <Image
        src={`${CDN}/shared/logo/logo_title_two_colors.png`}
        loading="eager"
        alt="gumgum.gg OPTCG fan site"
        className="hidden sm:flex"
        height={50}
        width={100}
      />
    </>
  );
}
